import axios from 'axios';
import type {
  ApiResponse,
  AnalyzeResponse,
  GenerateResponse,
  GenerateOutlineRequest,
} from '../../../shared/types';
import { useAuthStore } from '../store/auth';

// 默认端点
const DEFAULT_API_URL = 'https://novel-outline-api.yaoyouwei7.workers.dev';

// 设置存储键
const SETTINGS_KEY = 'novel-outline-settings';
const ENDPOINT_KEY = 'novel-outline-endpoint';

// 获取用户配置的端点
function getApiBaseUrl(): string {
  const stored = localStorage.getItem(ENDPOINT_KEY);
  return stored || DEFAULT_API_URL;
}

// 获取用户的 LLM 配置
interface LLMSettings {
  apiKeys: {
    openai?: string;
    glm?: string;
    minimax?: string;
    [key: string]: string | undefined;
  };
  defaultBackend: string;
  apiEndpoint?: string;
}

function getLLMConfig(): { backend: string; apiKey: string; baseUrl?: string; model?: string } | null {
  const saved = localStorage.getItem(SETTINGS_KEY);
  if (!saved) return null;

  try {
    const settings = JSON.parse(saved) as LLMSettings;
    const backend = settings.defaultBackend;
    const apiKey = settings.apiKeys[backend];

    if (!apiKey || !backend) return null;

    const baseUrl = settings.apiKeys[`${backend}BaseUrl`];
    const model = settings.apiKeys[`${backend}Model`];

    return {
      backend,
      apiKey,
      baseUrl: baseUrl as string | undefined,
      model: model as string | undefined,
    };
  } catch {
    return null;
  }
}

function getAuthBaseUrl(): string {
  return getApiBaseUrl();
}

const AUTH_BASE_URL = getAuthBaseUrl();

// 创建 axios 实例（动态获取 baseURL）
const createApi = () => axios.create({
  baseURL: `${getApiBaseUrl()}/api`,
  timeout: 180000, // 3 分钟，大纲生成需要更长时间
  headers: {
    'Content-Type': 'application/json',
  },
});

// 获取 API 实例
const getApi = () => {
  const api = createApi();

  // 请求拦截器 - 自动添加认证 API Key
  api.interceptors.request.use((config) => {
    const apiKey = useAuthStore.getState().apiKey;
    if (apiKey) {
      config.headers['X-API-Key'] = apiKey;
    }
    return config;
  });

  // 响应拦截器
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.data?.error) {
        return Promise.reject(new Error(error.response.data.error.message));
      }
      return Promise.reject(error);
    }
  );

  return api;
};

// ==================== 认证 API ====================

interface LoginResponse {
  user: {
    id: string;
    email: string;
    name?: string;
    plan: string;
    credits: number;
  };
  apiKeys: Array<{
    id: string;
    name: string;
    prefix: string;
    usage_count: number;
  }>;
}

interface RegisterResponse {
  user: {
    id: string;
    email: string;
    name?: string;
    plan: string;
    credits: number;
  };
  apiKey: string;
  message: string;
}

/**
 * 用户注册
 */
export async function register(email: string, password: string, name?: string): Promise<RegisterResponse> {
  const response = await axios.post<RegisterResponse>(`${AUTH_BASE_URL}/auth/register`, {
    email,
    password,
    name,
  });
  return response.data;
}

/**
 * 用户登录
 */
export async function login(email: string, password: string): Promise<LoginResponse> {
  const response = await axios.post<LoginResponse>(`${AUTH_BASE_URL}/auth/login`, {
    email,
    password,
  });
  return response.data;
}

/**
 * 获取用户信息
 */
export async function getProfile(): Promise<{ user: LoginResponse['user'] }> {
  const api = getApi();
  const response = await api.get<{ user: LoginResponse['user'] }>('/auth/profile');
  return response.data;
}

/**
 * 获取用户的 API Keys
 */
export async function getApiKeys(): Promise<{ apiKeys: LoginResponse['apiKeys'] }> {
  const api = getApi();
  const response = await api.get<{ apiKeys: LoginResponse['apiKeys'] }>('/auth/keys');
  return response.data;
}

/**
 * 创建新的 API Key
 */
export async function createApiKey(name: string): Promise<{ key: string }> {
  const api = getApi();
  const response = await api.post<{ key: string }>('/auth/keys', { name });
  return response.data;
}

// ==================== 业务 API ====================

/**
 * 分析参考文本
 */
export async function analyzeText(text: string, title?: string): Promise<AnalyzeResponse> {
  const api = getApi();
  const llmConfig = getLLMConfig();

  const response = await api.post<ApiResponse<AnalyzeResponse>>('/analyze', {
    text,
    title,
    ...(llmConfig && {
      backend: llmConfig.backend,
      apiKey: llmConfig.apiKey,
      baseUrl: llmConfig.baseUrl,
      model: llmConfig.model,
    }),
  });

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error?.message || '分析失败');
  }
  return response.data.data;
}

/**
 * 获取历史分析结果
 */
export async function getAnalysis(id: string): Promise<AnalyzeResponse> {
  const api = getApi();
  const response = await api.get<ApiResponse<AnalyzeResponse>>(`/analyze/${id}`);
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error?.message || '获取失败');
  }
  return response.data.data;
}

/**
 * 生成大纲
 */
export async function generateOutline(
  request: GenerateOutlineRequest
): Promise<GenerateResponse> {
  const api = getApi();
  const llmConfig = getLLMConfig();

  if (!llmConfig) {
    throw new Error('请先在设置页面配置 LLM API Key');
  }

  const response = await api.post<ApiResponse<GenerateResponse>>('/generate', {
    ...request,
    backend: llmConfig.backend,
    apiKey: llmConfig.apiKey,
    baseUrl: llmConfig.baseUrl,
    model: llmConfig.model,
  });

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error?.message || '生成失败');
  }
  return response.data.data;
}

/**
 * 获取已生成的大纲
 */
export async function getOutline(id: string): Promise<GenerateResponse> {
  const api = getApi();
  const response = await api.get<ApiResponse<GenerateResponse>>(`/generate/${id}`);
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error?.message || '获取失败');
  }
  return response.data.data;
}

/**
 * 重新生成大纲
 */
export async function regenerateOutline(
  id: string,
  feedback: string
): Promise<GenerateResponse> {
  const api = getApi();
  const llmConfig = getLLMConfig();

  const response = await api.post<ApiResponse<GenerateResponse>>(
    `/generate/${id}/regenerate`,
    {
      feedback,
      ...(llmConfig && {
        backend: llmConfig.backend,
        apiKey: llmConfig.apiKey,
        baseUrl: llmConfig.baseUrl,
        model: llmConfig.model,
      }),
    }
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error?.message || '重新生成失败');
  }
  return response.data.data;
}

// ==================== V3 API ====================

interface V3UploadResponse {
  success: boolean;
  bookId: string;
  title: string;
  chapters: Array<{
    index: number;
    title: string;
    wordCount: number;
  }>;
  metadata: {
    totalChapters: number;
    totalWords: number;
    encoding: string;
  };
}

interface V3AnalyzeResponse {
  success: boolean;
  ruleSetId: string;
  status: string;
  rules: any;
}

interface V3GenerateResponse {
  success: boolean;
  outlineId: string;
  outline: {
    title: string;
    genre: string;
    synopsis: string;
    chapters: Array<{
      index: number;
      title: string;
      summary: string;
      wordCount: number;
      keyPoints: string[];
    }>;
  };
  goldenChapters: {
    chapter1: string;
    chapter2: string;
    chapter3: string;
  };
}

interface V3GenerateRequest {
  ruleSetId: string;
  userInput: {
    genre: string;
    desiredWords: number;
    style?: string;
    customRequirements?: string;
  };
}

/**
 * V3: 上传书籍文件
 */
export async function v3UploadBook(file: File): Promise<V3UploadResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const apiEndpoint = getApiBaseUrl();
  const response = await fetch(`${apiEndpoint}/api/v3/upload`, {
    method: 'POST',
    body: formData,
  });

  const result = await response.json();
  if (!result.success) {
    throw new Error(result.error?.message || '上传失败');
  }
  return result;
}

/**
 * V3: 分析书籍提取规则
 */
export async function v3AnalyzeBook(bookId: string, options?: { fastMode?: boolean; sampleSize?: number }): Promise<V3AnalyzeResponse> {
  const api = getApi();
  const response = await api.post<ApiResponse<V3AnalyzeResponse>>('/v3/analyze', {
    bookId,
    options,
  });

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error?.message || '分析失败');
  }
  return response.data.data;
}

/**
 * V3: 基于规则生成大纲
 */
export async function v3GenerateOutline(request: V3GenerateRequest): Promise<V3GenerateResponse> {
  const api = getApi();
  const response = await api.post<ApiResponse<V3GenerateResponse>>('/v3/generate', request);

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error?.message || '生成失败');
  }
  return response.data.data;
}

export default getApi();
